<template>
  <div class="initiatives-detail-page fill-height" >
    <transition mode="out-in">
      <div key="loaded" v-if="Initiative && Initiatives">
        <div class="pt-200 px-6 px-md-16" :style="{maxWidth: '1200px', margin: '0 auto', position: 'relative'}">
          <div class="hero-background" v-if="$vuetify.breakpoint.mdAndUp"><span v-for="i in 12" :key="i">{{Initiative[`${$language.current}Title`]}}&nbsp;</span></div>
          <v-row>
            <v-col cols="12" md="4">
              <transition appear name="from-below">
                <seo-v-img style="--appear-delay: 100ms;" cover :src="`/media/${Initiative.image.id}`" :lazy-src="lazyImgSrc(Initiative.image)" contain :aspect-ratio="aspect(Initiative.image)" />
              </transition>
            </v-col>

            <v-col class="pt-10 ml-4">
              <v-row align="center">
                <v-col cols="12" ><h1><CharSplitText style="--appear-delay: 200ms;" :text="hypenate(Initiative[`${$language.current}Title`])" /></h1></v-col>
                <transition appear name="from-below"><SocialRepeater v-if="Initiative.social" style="--appear-delay: 400ms;" class="grow ml-n2" :data="Initiative.social" /> </transition>
              </v-row>
            </v-col>
          </v-row>
          <!-- <v-row class="mt-10">
            <v-col cols="12" md="4">
            </v-col>
          </v-row> -->
        </div>
        <section class="my-xl-16" v-if="Initiative">
          <v-container>
            <v-row class="description">
              <v-col cols="12" sm="4">
                <seo-v-img class="mt-6" v-if="Initiative && Initiative.logo" width="150" :src="`/media/${Initiative && Initiative.logo.id}`" :lazy-src="JSON.parse(Initiative.logo.metaData)['image-lazy-src']"/>
              </v-col>
              <v-col cols="12" sm="8">
                <component
                  v-for="(block, index) in Initiative[`${$language.current}Content`]"
                  :is="repeaterComponents[block._repeaterType]"
                  :key="block.id"
                  :data="block"
                  :articles="Initiative && Initiative.articles"
                  :project-slug="slug"
                  invert-ui
                  :style="{'--appear-delay': `${300 + (index  * 100)}ms`}"
                  />
              </v-col>
            </v-row>
          </v-container>
        </section>
        <InitiativesDetailFooter v-if="Initiatives" :next-project="nextProject" :prev-project="prevProject"/>
      </div>
      <v-row v-else class="fill-height" align="center" justify="center" key="loading">
        <v-col class="shrink"><LoadingSpinner /></v-col>
      </v-row>
    </transition>
  </div>
</template>

<script>
import GetInitativeEN from '@/graphql/GetInitiative_EN.gql'
import GetInitativeIT from '@/graphql/GetInitiative_IT.gql'
import ListInitiativeIT from '@/graphql/ListInitiative_IT.gql'
import ListInitiativeEN from '@/graphql/ListInitiative_EN.gql'
import { MarkdownBlock } from '@monogrid/vue-lib'
import ContentImage from '@/atoms/ContentImage'
import ContentParagraph from '@/atoms/ContentParagraph'
import ContentTitle from '@/atoms/ContentTitle'
import ContentVideoYoutubePlayer from '@/atoms/ContentVideoYoutubePlayer'
import ContentVideoVimeoPlayer from '@/atoms/ContentVideoVimeoPlayer'
import ContentGallery2 from '@/atoms/ContentGallery2'
import ContentSocial from '@/atoms/ContentSocial'
import ContentArticleList from '@/atoms/ContentArticleList'
import { findIndex } from 'lodash/array'
import InitiativesDetailFooter from '@/molecules/InitiativesDetailFooter'
import InitiativesContentHeader from '@/molecules/InitiativesContentHeader'
import CharSplitText from '@/atoms/CharSplitText'
import LoadingSpinner from '@/atoms/LoadingSpinner'
import SocialRepeater from '@/molecules/SocialRepeater'

export default {
  name: 'InitiativesDetailPage',
  components: {
    SocialRepeater,
    InitiativesContentHeader,
    InitiativesDetailFooter,
    MarkdownBlock,
    CharSplitText,
    LoadingSpinner
  },
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      repeaterComponents: {
        Image: ContentImage,
        Paragraph: ContentParagraph,
        Title: ContentTitle,
        YoutubeVideo: ContentVideoYoutubePlayer,
        VimeoVideo: ContentVideoVimeoPlayer,
        Gallery: ContentGallery2,
        Social: ContentSocial,
        Articles: ContentArticleList
      },
      localizedQueries: {
        en: {
          get: GetInitativeEN,
          projectList: ListInitiativeEN
        },
        it: {
          get: GetInitativeIT,
          projectList: ListInitiativeIT
        }
      }
    }
  },
  computed: {
    heroContent () {
      return {
        image: this.Initiative.image,
        name: this.Initiative[`${this.$language.current}Title`],
        id: this.Initiative.id
      }
    },
    prevProject () {
      if (!this.Initiatives) return
      const projectsIndex = findIndex(this.Initiatives.items, { _slug: this.slug })
      if (projectsIndex === 0) return this.Initiatives.items[this.Initiatives.items.length - 1]
      return this.Initiatives.items[projectsIndex - 1]
    },
    nextProject () {
      if (!this.Initiatives) return
      const projectsIndex = findIndex(this.Initiatives.items, { _slug: this.slug })
      if (projectsIndex === this.Initiatives.items.length - 1) return this.Initiatives.items[0]
      return this.Initiatives.items[projectsIndex + 1]
    }
  },
  methods: {
    handleArticleClick (slug) {
      this.$router.push({ name: 'InitiativesArticle', params: { slug: slug, projectSlug: this.Initiative._slug } })
    },
    lazyImgSrc (image) {
      const meta = JSON.parse(image.metaData)
      return meta['image-lazy-src']
    },
    aspect (image) {
      const meta = JSON.parse(image.metaData)
      return parseFloat(meta['image-width']) / parseFloat(meta['image-height'])
    },
    hypenate (str) {
      return str.replace(/\u200B/g, '&shy;')
    }
  },
  apollo: {
    Initiative: {
      query () {
        return this.localizedQueries[this.$language.current].get
      },
      skip: false,
      variables () {
        return {
          slug: this.slug,
          status: 'PUBLISHED'
        }
      },
      result () {
        this.$root.$emit('scroll-to-saved-position')
      }
    },
    Initiatives: {
      variables () {
        return {
          sortOn: 'priority',
          status: 'PUBLISHED'
        }
      },
      query () {
        return this.localizedQueries[this.$language.current].projectList
      },
      skip: false
    }
  },

  metaInfo () {
    const pageName = 'initiatives'
    const shareDescriptionFallback = this.$root.siteSettings[`${pageName}PageShareDescription`]
    const shareDescription = this.Initiative ? this.Initiative[`${this.$language.current}ShareDescription`] || shareDescriptionFallback : shareDescriptionFallback

    const shareImage = this.Initiative && this.Initiative.shareImage ? this.Initiative.shareImage : null
    const shareImageFallback1 = this.Initiative && this.Initiative.image ? this.Initiative.image : null
    const shareImageFallback2 = this.$root.siteSettings[`${pageName}PageShareImage`]

    const finalShareImage = shareImage || shareImageFallback1 || shareImageFallback2

    const imageMeta = JSON.parse(finalShareImage.metaData)
    return {
      // home page browser title
      title: this.Initiative ? this.Initiative.name : '',
      meta: [
        { vmid: 'description', name: 'description', content: shareDescription },
        { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: `${this.Initiative ? this.Initiative.name : ''} | ${this.$root.siteSettings.siteTitle}` },
        { vmid: 'ogdescription', property: 'og:description', content: shareDescription },
        { vmid: 'ogimage', property: 'og:image', content: `https://${this.$root.process.env.SERVICE_TARGET_DOMAIN}/media/${finalShareImage.id}` },
        { vmid: 'ogimagetype', property: 'og:image:type', content: finalShareImage.mimeType },
        { vmid: 'ogimageheight', property: 'og:image:width', content: imageMeta['image-width'] },
        { vmid: 'ogimagewidth', property: 'og:image:height', content: imageMeta['image-height'] }
      ]
    }
  }
}
</script>

<style scoped lang="scss">

.hero-background {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50%, -50%);
  white-space: nowrap;
  font-size: 10vw;
  opacity: 0.05;
  font-family: $heading-font-family;
  user-select: none;
  pointer-events: none;
  animation: hero-title-motion 20s linear forwards infinite;
}

.initiatives-detail-page {
  overflow: hidden;

  .article {
    &:nth-child(even) {
      padding-top: 100px;
    }

    .wrapper {
      max-width: 400px;
      overflow: hidden;
      cursor: pointer;
    }

    h2 {
      font-family: $heading-font-family;
      font-size: 2rem;
    }
  }
}
</style>
